import { useEffect, useRef } from 'react';
import axios from 'api/axios';
import { V2_API_PATH } from 'api/apiPath';

// Must match IDLE_TIMEOUT_MINUTES on both backends (api_cloudhub_panel & cloudhub).
// If this is larger than the server's value the user gets server-side kicked
// before the client stops sending heartbeats; smaller means the client stops
// pinging while the server still tolerates idle, wasting the tail of the window.
const IDLE_LIMIT_MS = (Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES) || 10) * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
// Throttle activity bumps so high-frequency events (mousemove, wheel) don't
// thrash the timestamp. Sub-second precision is irrelevant when the heartbeat
// tick is 60s anyway.
const BUMP_THROTTLE_MS = 1000;

// canonical event set (shared with cloudhub iframe's app.blade.php):
//   mousedown is a superset of click (covers drag + long-press)
//   wheel instead of scroll: scroll fires on programmatic scrolling too
//     (smooth-scroll animations, anchor jumps), which would falsely refresh
//     the idle timer. Keyboard scrolling is covered by keydown.
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'wheel'
];
// capture: true catches the event even if a child widget calls stopPropagation
// on its way up the tree.
const LISTENER_OPTIONS: AddEventListenerOptions = { passive: true, capture: true };

const useIdleHeartbeat = (): void => {
  const lastActivityRef = useRef<number>(Date.now());
  const lastBumpRef = useRef<number>(0);

  useEffect(() => {
    const bump = (): void => {
      const now = Date.now();
      if (now - lastBumpRef.current < BUMP_THROTTLE_MS) return;
      lastBumpRef.current = now;
      lastActivityRef.current = now;
    };

    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, bump, LISTENER_OPTIONS);
    });
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, bump, LISTENER_OPTIONS);
      });
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const elapsedMs = Date.now() - lastActivityRef.current;
      // Truly idle — stop pinging and let the server-side middleware kick on
      // the next request (heartbeat, Polling, or user action).
      if (elapsedMs >= IDLE_LIMIT_MS) return;

      // Report seconds since real activity so the server records last_active_at
      // relative to true activity, not to ping arrival (decouples server-side
      // timeout precision from heartbeat cadence). Server clamps to its own
      // idle_timeout so a hostile client cannot extend session.
      const idleSeconds = Math.floor(elapsedMs / 1000);
      // V2_API_PATH constants are full paths from the host root (/api/v2/...);
      // override baseURL to REACT_APP_BASE_URL so we don't double the /api/v2
      // prefix that the default `axios` instance baseURL (REACT_APP_API_URL)
      // already carries.
      axios.post(V2_API_PATH.KEEP_ALIVE, { idle_seconds: idleSeconds }, {
        baseURL: process.env.REACT_APP_BASE_URL
      }).catch(() => {
        // 401 is handled by the v2 axios interceptor (refresh → logout chain).
        // Network blips and other errors are silently ignored so a single
        // failure doesn't break the heartbeat loop.
      });
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);
};

export default useIdleHeartbeat;
